"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { RiCheckboxCircleFill, RiErrorWarningLine } from "react-icons/ri";
import { Button } from "@/components/ui";
import { useUser } from "@/contexts/user-provider";
import { formatDate } from "@/lib/format-data";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import type { MarketingPhotoConsent } from "@/types/photo-studio";
import { marketingQueryOptions, useUpdateMarketingConsentMutation } from "@/api/provider/photo-studio/marketing";

// "Never asked" is not a yes: only an explicit Yes lets photos be exported.
const statusLabel = (consented: boolean | null | undefined) => {
  if (consented === true) return "Agreed to marketing photos";
  if (consented === false) return "Declined marketing photos";
  return "Not asked yet";
};
function recordedLine(consent: MarketingPhotoConsent | null | undefined) {
  if (!consent?.recordedAt) return null;
  const when = formatDate(consent.recordedAt);
  return consent.recordedByName ? `Recorded by ${consent.recordedByName} on ${when}` : `Recorded on ${when}`;
}
export function MarketingConsentCard(t0) {
  const $ = _c(35);
  const {
    patientId
  } = t0;
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasPermission(currentUser, PERMISSIONS.PATIENTS_PROFILES_WRITE);
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canEdit = t1;
  let t2;
  if ($[2] !== patientId) {
    t2 = marketingQueryOptions.consent(patientId);
    $[2] = patientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: consent,
    isLoading
  } = useQuery(t2);
  const update = useUpdateMarketingConsentMutation(patientId);
  let t3;
  if ($[4] !== update) {
    t3 = async consented => {
      ;
      try {
        await update.mutateAsync(consented);
      } catch (t4) {
        const error = t4;
        toast({
          variant: "error",
          title: "Could not save marketing consent",
          description: await parseError(error)
        });
      }
    };
    $[4] = update;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const save = t3;
  const consented_0 = consent?.consented;
  let t4;
  if ($[6] !== consent) {
    t4 = recordedLine(consent);
    $[6] = consent;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const recorded = t4;
  const t5 = consented_0 === true ? "border-status-success-text/30 bg-status-success-bg" : "border-border bg-background-secondary";
  let t6;
  if ($[8] !== t5) {
    t6 = cx("flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between", t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== consented_0) {
    t7 = consented_0 === true ? <RiCheckboxCircleFill className="mt-0.5 size-4 shrink-0 text-status-success-text" /> : <RiErrorWarningLine className="mt-0.5 size-4 shrink-0 text-status-warning-text" />;
    $[10] = consented_0;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== consented_0 || $[13] !== isLoading) {
    t8 = isLoading ? "Checking consent..." : statusLabel(consented_0);
    $[12] = consented_0;
    $[13] = isLoading;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <p className="text-body-strong text-foreground">{t8}</p>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = recorded ?? "The patient must agree before their photos are used in marketing.";
  let t11;
  if ($[17] !== t10) {
    t11 = <p className="text-caption text-foreground-muted">{t10}</p>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t9) {
    t12 = <div>{t9}{t11}</div>;
    $[19] = t11;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t7) {
    t13 = <div className="flex items-start gap-2">{t7}{t12}</div>;
    $[22] = t12;
    $[23] = t7;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== canEdit || $[26] !== consented_0 || $[27] !== isLoading || $[28] !== save || $[29] !== update) {
    t14 = canEdit && !isLoading && <div className="flex shrink-0 gap-2"><Button size="sm" variant={consented_0 === true ? "secondary" : "primary"} disabled={update.isPending || consented_0 === true} onClick={() => void save(true)}>Patient agreed</Button><Button size="sm" variant="primary" disabled={update.isPending || consented_0 === false} onClick={() => void save(false)}>Patient declined</Button></div>;
    $[25] = canEdit;
    $[26] = consented_0;
    $[27] = isLoading;
    $[28] = save;
    $[29] = update;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t13 || $[32] !== t14 || $[33] !== t6) {
    t15 = <div className={t6}>{t13}{t14}</div>;
    $[31] = t13;
    $[32] = t14;
    $[33] = t6;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
}